import { useContext, useMemo } from 'react';
import { BeerContext } from '../BeerContext';
import { Beer } from '../types';
import { useFilters } from './useFilters';
import { useUserInteractions } from './useUserInteractions';
import { useBeerData } from './useBeerData';
import { useUiState } from './useUiState';
import { useTabFiltering } from './useTabFiltering';

/**
 * Hook to access filtered beers based on current filters and user interactions
 * This follows Interface Segregation Principle by providing a focused API
 */
export function useFilteredBeers(): Beer[] {
  const { state } = useContext(BeerContext) || { state: null };
  const { filters } = useFilters();
  const { beers } = useBeerData();
  const activeTab = useActiveTab();
  
  // Call useTabFiltering at the top level of the hook, not inside useMemo
  // Pass the showOnlyFavorites filter to ensure proper filtering in My Drinks tab
  const tabFilteredBeers = useTabFiltering(beers, activeTab, filters.showOnlyFavorites, filters.filterByFavorites);
  
  
  if (!state) {
    throw new Error('useFilteredBeers must be used within a BeerProvider');
  }
  
  // Apply filters to beers
  return useMemo(() => {
    if (!beers || beers.length === 0) {
      return [];
    }
    
    // Use the tabFilteredBeers that were obtained at the top level
    
    // Then apply regular filters
    const result = tabFilteredBeers.filter(beer => {
      // Search term filter
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.trim();
        const searchLower = searchTerm.toLowerCase();
        const isNumericSearch = /^[0-9]+$/.test(searchTerm);

        const matchesNumber = isNumericSearch
          ? String(beer.number).startsWith(searchTerm)
          : false;

        const matchesText =
          beer.name.toLowerCase().includes(searchLower) ||
          beer.brewery.toLowerCase().includes(searchLower) ||
          (beer.tags?.some(tag => tag.toLowerCase().includes(searchLower)) ?? false);

        if (!matchesNumber && !matchesText) {
          return false;
        }
      }
      
      // Type filter
      if (filters.typeFilter && beer.style !== filters.typeFilter) {
        return false;
      }
      
      // Brewery filter
      if (filters.breweryFilter && beer.brewery !== filters.breweryFilter) {
        return false;
      }
      
      // Drink type filter
      if (filters.drinkTypeFilter && beer.type !== filters.drinkTypeFilter) {
        return false;
      }
      
      // Availability filter
      if (filters.availabilityFilter && beer.availability !== filters.availabilityFilter) {
        return false;
      }
      
      // Tag filters
      if (filters.tagFilters.length > 0) {
        const hasAllTags = filters.tagFilters.every(tag => 
          beer.tags && beer.tags.includes(tag)
        );
        
        if (!hasAllTags) {
          return false;
        }
      }
      
      // Note: showOnlyFavorites filter is handled in useTabFiltering for My Beers tab
      // where it filters by tasting list instead of favorites
      
      // Show only available filter
      if (filters.showOnlyAvailable && beer.availability !== 'available') {
        return false;
      }
      
      return true;
    });
    

    return result;
  }, [beers, filters, tabFilteredBeers]);
}

/**
 * Hook to access loading state
 */
export function useLoadingState(): boolean {
  const { state } = useContext(BeerContext) || { state: null };
  
  if (!state) {
    throw new Error('useLoadingState must be used within a BeerProvider');
  }
  
  return state.loading;
}

/**
 * Hook to access error state
 */
export function useErrorState(): Error | null {
  const { state } = useContext(BeerContext) || { state: null };
  
  if (!state) {
    throw new Error('useErrorState must be used within a BeerProvider');
  }
  
  return state.error;
}

/**
 * Hook to access user interactions for a specific beer
 * Follows Single Responsibility Principle by focusing only on beer-specific interactions
 */
export function useBeerInteractions(beerId: string) {
  const { userInteractions } = useUserInteractions();
  
  return useMemo(() => ({
    isFavorite: userInteractions.favorites.includes(beerId),
    rating: userInteractions.ratings[beerId] || 0,
    notes: userInteractions.notes[beerId] || '',
    isInTastingList: userInteractions.tastingList.includes(beerId)
  }), [userInteractions, beerId]);
}

/**
 * Hook to access active tab
 */
export function useActiveTab(): string {
  const { ui } = useUiState();
  return ui.activeTab;
}

/**
 * Main selector hook that combines all the individual hooks
 * This makes it easier to use in components that need multiple pieces of state
 * While still maintaining the SOLID principles in the underlying implementation
 */
export function useBeerSelectors() {
  const filteredBeers = useFilteredBeers();
  const loading = useLoadingState();
  const error = useErrorState();
  const activeTab = useActiveTab();
  
  return {
    filteredBeers,
    loading,
    error,
    activeTab,
    useBeerInteractions, // Include the function itself for component use
  };
}
